import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import PageTabs from '../../../components/page-tabs/page-tabs.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import ActionButton from '../../../components/action-button/action-button.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import ContextMenu from '../../../components/context-menu/context-menu.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import List from '../../../components/list/list.component';
import ListItem from '../../../components/list-item/list-item.component';
import ListItemText from '../../../components/list-item-text/list-item-text.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import ActionButtonContainer from '../../../components/action-button-container/action-button-container.component';
import ActionButtonNewItem from '../../../components/action-button-new-item/action-button-new-item.component';
import IconTextBlock from '../../../components/icon-text-block/icon-text-block.component';
import TableRowNoContent from '../../../components/table-row-no-content/table-row-no-content.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import DealValuationFileRow from './deal-valuation-file-row/deal-valuation-file-row.component';
import DealValuationFileRowForm from './deal-valuation-file-row-form/deal-valuation-file-row-form.component';
import DeleteItemIcon from '../../../icons/delete-item-icon';
import RemoveIcon from '../../../icons/remove-icon';
import ArrowLinkIcon from '../../../icons/arrow-link-icon';
import DownloadIcon from '../../../icons/download-icon';
import StructureService from '../../../services/structure-service';
import ScreenService from '../../../services/screen-service';
import useScreenSmall from '../../../hooks/use-screen-small';
import { DATA_DEAL_VALUATION_FILE_ID } from '../../../constants/system/data-attributes';
import {
  DEAL_VALUATION_FILE_CREATE, DEAL_VALUATION_FILE_DELETE, DEAL_VALUATION_FILE_EDIT, DEAL_VALUATION_FILE_LIST_VIEW,
} from '../../../constants/entity/permissions';

const DealValuationFiles: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 4;

  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isNewDeal,
    isVisible,
    isRightDrawerOpen,
    queryDealFileId,
    dealValuationFilesData,
    dealValuationFileFiles,
    dealValuationFileNewItems,
    dealValuationFileActiveIds,
    dealValuationFileInlineSaveId,
    dealValuationFileGroupInlineSaveId,
    onFileChange,
    onFileDelete,
    onFileViewClick,
    onAddNewItem,
    onDeleteClick,
    onOpenDeleteConfirmModal,
    onOpenModal,
    onInlineSave,
    onInlineCancel,
    onInlineEditModeButtonClick,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);

  const dealValuationFileEditPermissions: string[] = useMemo((): string[] => [DEAL_VALUATION_FILE_EDIT], []);
  const dealValuationFileListViewPermissions: string[] = useMemo((): string[] => [DEAL_VALUATION_FILE_LIST_VIEW], []);
  const dealValuationFileCreatePermissions: string[] = useMemo((): string[] => [DEAL_VALUATION_FILE_CREATE], []);

  const fileDropDeps: DepsItem[] = useMemo((): DepsItem[] => (
    [dealValuationFileNewItems, dealValuationFileFiles]
  ), [dealValuationFileNewItems, dealValuationFileFiles]);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: DEAL_VALUATION_FILE_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealValuationFileId: number): void => {
        if (dealValuationFileId) {
          onOpenDeleteConfirmModal(event, dealValuationFileId);
        }
      },
    },
  ], []);

  const addNewItem: Function = useCallback((dealValuationFileGroupId: number): void => {
    if (onAddNewItem) {
      onAddNewItem(dealValuationFileGroupId, {
        id: StructureService.getNewItemId(dealValuationFileNewItems[dealValuationFileGroupId] || []),
        groupId: dealValuationFileGroupId,
        name: '',
        file: '',
      });
    }
  }, [dealValuationFilesData, dealValuationFileNewItems]);

  const deleteClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealValuationFileId: number, dealValuationFileGroupId: number,
  ): void => {
    if (onDeleteClick) {
      onDeleteClick(dealValuationFileId, dealValuationFileGroupId);
    }

    event.stopPropagation();
  }, [dealValuationFileNewItems]);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const openModal: Function = useCallback((
    dealValuationFile: DealFile, dealValuationFileGroup: DealFileGroup, isNewItem?: boolean,
  ): void => {
    if (onOpenModal) {
      onOpenModal(dealValuationFile, dealValuationFileGroup, isNewItem);
    }
  }, []);

  const onRowClick: Function = useCallback((
    dealValuationFile: DealFile, dealValuationFileGroup: DealFileGroup,
  ): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection && !isEditMode) {
      openModal(dealValuationFile, dealValuationFileGroup);
    }
  }, [isEditMode]);

  const tabs: TabItem[] = dealValuationFilesData && !!dealValuationFilesData.length
    ? dealValuationFilesData.map((dealValuationFileGroup: DealFileGroup): TabItem => ({
      id: dealValuationFileGroup.id,
      name: `deal-valuation-file-group-${dealValuationFileGroup.id}`,
      title: dealValuationFileGroup.name,
      component: (
        <CheckAccess permissions={dealValuationFileListViewPermissions}>
          <Card>
            {
              !isSmallScreen ? (
                <CheckAccess permissions={dealValuationFileEditPermissions} isEnabled={isEditMode || isNewDeal}>
                  <Table isFullWidth={isEditMode || isNewDeal}>
                    <TableBody
                      isPlain
                      withZebraRows
                      withFixedCellsSizeExtended={isEditMode || isNewDeal}
                    >
                      {
                        dealValuationFileGroup.files && !!dealValuationFileGroup.files.length
                        && dealValuationFileGroup.files.map((dealValuationFile: DealFile): ReactElement => {
                          const isActive: boolean = dealValuationFileActiveIds
                            && dealValuationFileActiveIds.includes(dealValuationFile.id);
                          const isInlineSaving: boolean = dealValuationFileInlineSaveId !== null
                          && dealValuationFileGroupInlineSaveId !== null
                            ? dealValuationFile.id === dealValuationFileInlineSaveId
                            && dealValuationFileGroup.id === dealValuationFileGroupInlineSaveId
                            : isSaving;

                          return (
                            <DealValuationFileRow
                              isActive={isActive}
                              isInlineSaving={isInlineSaving}
                              isSaveDisabled={isSaveDisabled}
                              isSaving={isSaving}
                              isNewDeal={isNewDeal}
                              isEditMode={isEditMode}
                              key={dealValuationFile.id}
                              fileDropDeps={fileDropDeps}
                              actionMenuItems={actionMenuItems}
                              dealValuationFileItem={dealValuationFile}
                              dealValuationFileGroup={dealValuationFileGroup}
                              dealValuationFileFile={(
                                dealValuationFileFiles[dealValuationFileGroup.id]
                                && dealValuationFileFiles[dealValuationFileGroup.id][dealValuationFile.id]
                              )}
                              dealValuationFileActiveIds={dealValuationFileActiveIds}
                              onRowClick={onRowClick}
                              onOpenActionMenu={openActionMenu}
                              onFileChange={onFileChange}
                              onFileDelete={onFileDelete}
                              onFileViewClick={onFileViewClick}
                              onInlineSave={onInlineSave}
                              onInlineCancel={onInlineCancel}
                              onInlineEditModeButtonClick={onInlineEditModeButtonClick}
                            />
                          );
                        })
                      }
                      {
                        (!isEditMode && !isNewDeal) && (!dealValuationFileGroup.files
                          || !dealValuationFileGroup.files.length) && (
                          <TableRowNoContent>
                            <TableCell colSpan={COLUMNS_TOTAL}>
                              No files in this group
                            </TableCell>
                          </TableRowNoContent>
                        )
                      }
                      {
                        (isEditMode || isNewDeal) && dealValuationFileNewItems
                        && dealValuationFileNewItems[dealValuationFileGroup.id]
                        && !!dealValuationFileNewItems[dealValuationFileGroup.id].length
                        && dealValuationFileNewItems[dealValuationFileGroup.id]
                          .map((dealValuationFile: DealFile): ReactElement => (
                            <DealValuationFileRowForm
                              isNewEntity
                              isActive={!isNewDeal}
                              isSaving={isSaving}
                              isSaveDisabled={isSaveDisabled}
                              isInlineSaving={(
                                dealValuationFileInlineSaveId !== null
                                && dealValuationFileGroupInlineSaveId !== null
                                  ? dealValuationFile.id === dealValuationFileInlineSaveId
                                  && dealValuationFileGroup.id === dealValuationFileGroupInlineSaveId
                                  : isSaving
                              )}
                              key={dealValuationFile.id}
                              fileDropDeps={fileDropDeps}
                              dealValuationFileData={dealValuationFile}
                              dealValuationFileGroupData={dealValuationFileGroup}
                              dealValuationFileFile={(
                                dealValuationFileFiles[dealValuationFileGroup.id]
                                && dealValuationFileFiles[dealValuationFileGroup.id][dealValuationFile.id]
                              )}
                              onFileChange={onFileChange}
                              onFileDelete={onFileDelete}
                              onOpenModal={(): void => {
                                onRowClick(dealValuationFile, dealValuationFileGroup);
                              }}
                              onDeleteClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                deleteClick(event, dealValuationFile.id, dealValuationFileGroup.id);
                              }}
                              onInlineSave={onInlineSave}
                              onInlineCancel={onInlineCancel}
                            />
                          ))
                      }
                      {
                        (isEditMode || isNewDeal) && (
                          <CheckAccess permissions={dealValuationFileCreatePermissions}>
                            <TableRowAddNewItem
                              label="Add New File"
                              colSpan={COLUMNS_TOTAL}
                              onClick={(): void => {
                                addNewItem(dealValuationFileGroup.id);
                              }}
                            />
                          </CheckAccess>
                        )
                      }
                    </TableBody>
                  </Table>
                </CheckAccess>
              ) : (
                <CheckAccess permissions={dealValuationFileEditPermissions} isEnabled={isEditMode || isNewDeal}>
                  <Card withMediumShadow>
                    <List>
                      {
                        dealValuationFileGroup.files && !!dealValuationFileGroup.files.length
                        && dealValuationFileGroup.files.map((dealValuationFile: DealFile): ReactElement => (
                          <ListItem
                            isButton
                            isExtended
                            key={dealValuationFile.id}
                            onClick={(): void => {
                              openModal(dealValuationFile, dealValuationFileGroup);
                            }}
                          >
                            <ListItemText
                              isBlock
                              subText={!isEditMode && !isNewDeal ? dealValuationFile.file.date : ''}
                            >
                              {dealValuationFile.file && dealValuationFile.file.name}
                            </ListItemText>
                            <ListItemText isBlock isBlockRight>
                              {
                                !isEditMode && !isNewDeal ? (
                                  dealValuationFile.file && (
                                    <FlexContainer isInline>
                                      <FilteredLink
                                        isExternal
                                        link={dealValuationFile.file.link}
                                        onMouseDown={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                          onFileViewClick(event, dealValuationFile.file, dealValuationFileGroup.name);
                                        }}
                                      >
                                        <IconTextBlock icon={<DownloadIcon />} />
                                      </FilteredLink>
                                    </FlexContainer>
                                  )
                                ) : (
                                  <ActionButtonContainer>
                                    <Tooltip title="Open in modal">
                                      <ActionButton
                                        withSmallPadding
                                        onClick={(): void => {
                                          onRowClick(dealValuationFile, dealValuationFileGroup);
                                        }}
                                      >
                                        <ArrowLinkIcon />
                                      </ActionButton>
                                    </Tooltip>
                                  </ActionButtonContainer>
                                )
                              }
                            </ListItemText>
                          </ListItem>
                        ))
                      }
                      {
                        (!isEditMode && !isNewDeal)
                        && (!dealValuationFileGroup.files || !dealValuationFileGroup.files.length) && (
                          <NoContentBlock>
                            No files in this group
                          </NoContentBlock>
                        )
                      }
                      {
                        (isEditMode || isNewDeal) && dealValuationFileNewItems[dealValuationFileGroup.id]
                        && dealValuationFileNewItems[dealValuationFileGroup.id]
                          .map((dealValuationFile: DealFile): ReactElement => (
                            <ListItem
                              isButton
                              isExtended
                              isActive={!isNewDeal}
                              key={dealValuationFile.id}
                              onClick={(): void => {
                                openModal(dealValuationFile, dealValuationFileGroup, true);
                              }}
                            >
                              <ListItemText isBlock>
                                New File
                              </ListItemText>
                              <ListItemText isBlock isBlockRight>
                                <ActionButtonContainer>
                                  <Tooltip title="Delete">
                                    <ActionButton
                                      withSmallPadding
                                      onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                        deleteClick(event, dealValuationFile.id, dealValuationFileGroup.id);
                                      }}
                                    >
                                      <RemoveIcon />
                                    </ActionButton>
                                  </Tooltip>
                                  <Tooltip title="Open in modal">
                                    <ActionButton
                                      withSmallPadding
                                      onClick={(): void => {
                                        onRowClick(dealValuationFile, dealValuationFileGroup);
                                      }}
                                    >
                                      <ArrowLinkIcon />
                                    </ActionButton>
                                  </Tooltip>
                                </ActionButtonContainer>
                              </ListItemText>
                            </ListItem>
                          ))
                      }
                      {
                        (isEditMode || isNewDeal) && (
                          <CheckAccess permissions={dealValuationFileCreatePermissions}>
                            <Card
                              isContainer
                              isSelectable
                              withSmallPadding
                              onClick={(): void => {
                                addNewItem(dealValuationFileGroup.id);
                              }}
                            >
                              <ActionButtonNewItem withNoHighlight label="Add New File" />
                            </Card>
                          </CheckAccess>
                        )
                      }
                    </List>
                  </Card>
                </CheckAccess>
              )
            }
          </Card>
        </CheckAccess>
      ),
    })) : [];

  useEffect((): void => {
    if (queryDealFileId && dealValuationFilesData && dealValuationFilesData.length) {
      dealValuationFilesData.forEach((dealValuationFileGroup: DealFileGroup): void => {
        if (dealValuationFileGroup.files && dealValuationFileGroup.files.length) {
          dealValuationFileGroup.files.forEach((dealValuationFile: DealFile): void => {
            if (dealValuationFile.file.link && dealValuationFile.file.id === queryDealFileId) {
              ScreenService.downloadFile(dealValuationFile.file.link);
            }
          });
        }
      });
    }
  }, [queryDealFileId]);

  return (
    <CheckAccess permissions={dealValuationFileEditPermissions} isEnabled={isEditMode || isNewDeal}>
      <div>
        {
          isVisible && (
            <PageTabs isSubTabs isCompressed={isRightDrawerOpen} tabs={tabs} />
          )
        }
      </div>
      <ContextMenu
        menuAnchor={menuAnchor}
        actionMenuItems={actionMenuItems}
        dataAttributeEntityId={DATA_DEAL_VALUATION_FILE_ID}
        onClose={closeActionMenu}
      />
    </CheckAccess>
  );
});

export default DealValuationFiles;
